import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { extractContactIntelligence } from '@/utils/contactIntelligence';
import type { ProspectList } from '@/hooks/useOptimizedSupabaseProspectLists';
import { derivePhones, getPhoneLabels } from '@/utils/derivePhones';
import {
  Phone,
  Mail,
  Calendar,
  MessageSquare,
  ExternalLink,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
  FileText,
  ChevronDown,
  ChevronUp,
  Tag,
  X,
  Hand,
  Archive,
  Trash2,
  Building2,
  Download,
} from 'lucide-react';
import {
  formatTimestamp,
  getActionDeadline,
  getManualDeadline,
  getActionDeadlineDate,
  getUrgencyFromAction,
} from '@/lib/time';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { OutboundContactSummary } from './OutboundContactSummary';
import { InboundContactSummary } from './InboundContactSummary';
import { getCachedVapiCall, setCachedVapiCall } from '@/utils/vapiCache';

interface CallAction {
  id: string;
  call_id: string;
  campaign_id: string;
  prospect_id: string;
  type: string;
  confidence: number;
  phrase: string | null;
  action_data: any;
  processed: boolean;
  created_at: string;
  status?: string;
  completed_at?: string;
  archived_at?: string;
  archived_until?: string;
  resolved_intelligence?: {
    name?: string;
    businessHeader?: string;
    phone?: string;
    email?: string;
    company?: string;
    businessIntent?: string;
    recipient?: string;
    processed_source?: string;
    extracted_at?: string;
  } | null;
  intelligence_processed_at?: string | null;
  campaign?: {
    name: string;
    type?: string;
    businessContext?: {
      company: string;
      industry: string;
      context: string;
    };
    client_id?: string;
    client_name?: string;
  };
  mode?: string;
}

interface ContactInfo {
  name?: string;
  phone?: string;
  email?: string;
  company?: string;
}

interface VapiCallData {
  recordingUrl?: string;
  transcript?: string;
  duration?: number;
  status?: string;
  contactInfo?: ContactInfo;
}

interface ContactCardProps {
  contactKey: string;
  actions: CallAction[];
  processedActions?: CallAction[];
  onMarkActionDone?: (actionId: string) => void;
  onToggleActionProcessed?: (actionId: string) => void;
  onUpdateDueDate?: (actionId: string, dueAt: string) => Promise<void>;
  onArchive?: () => void;
  onDelete?: () => void;
  isArchived?: boolean;
  prospectLists?: ProspectList[];
}

export function ContactCard({
  contactKey,
  actions,
  processedActions = [],
  onMarkActionDone,
  onToggleActionProcessed,
  onUpdateDueDate,
  onArchive,
  onDelete,
  isArchived = false,
  prospectLists = [],
}: ContactCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [vapiData, setVapiData] = useState<Record<string, VapiCallData>>({});
  const [loadingCalls, setLoadingCalls] = useState<Set<string>>(new Set());
  const [contactTags, setContactTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [openCallDetailsId, setOpenCallDetailsId] = useState<string | null>(
    null,
  );
  const { toast } = useToast();

  // Load tags from localStorage on mount
  useEffect(() => {
    const savedTags = localStorage.getItem(`contact-tags-${contactKey}`);
    if (savedTags) {
      try {
        setContactTags(JSON.parse(savedTags));
      } catch (e) {
        console.error('Failed to parse saved tags:', e);
      }
    }
  }, [contactKey]);

  // Save tags to localStorage when they change
  useEffect(() => {
    if (contactTags.length > 0) {
      localStorage.setItem(
        `contact-tags-${contactKey}`,
        JSON.stringify(contactTags),
      );
    } else {
      localStorage.removeItem(`contact-tags-${contactKey}`);
    }
  }, [contactTags, contactKey]);

  // Memoize unique call IDs to prevent infinite loops
  const uniqueCallIds = useMemo(
    () => [...new Set(actions.map((a) => a.call_id))],
    [actions],
  );

  // Memoize contact intelligence with prospect data integration - prefer stored intelligence
  const contactIntelligence = useMemo(() => {
    // First check if any action has stored intelligence
    const allActions = [...actions, ...processedActions];
    const storedIntelligence = allActions.find(
      (action) => action.resolved_intelligence,
    );

    if (storedIntelligence?.resolved_intelligence) {
      console.debug('[ContactCard] Using stored intelligence for', contactKey);
      return storedIntelligence.resolved_intelligence;
    }

    // Fallback to live processing if no stored intelligence
    console.debug(
      '[ContactCard] No stored intelligence found, processing live for',
      contactKey,
    );

    const latestCallId = uniqueCallIds[0];
    const transcript = latestCallId
      ? vapiData[latestCallId]?.transcript
      : undefined;

    // Get additional context from Vapi data and prospect lists
    const latestVapiData = latestCallId ? vapiData[latestCallId] : undefined;
    const prospectId = actions[0]?.prospect_id;

    // Find prospect in prospect lists
    let prospectData = null;
    if (prospectId && prospectLists) {
      for (const list of prospectLists) {
        const prospect = list.prospects?.find((p: any) => p.id === prospectId);
        if (prospect) {
          prospectData = prospect;
          break;
        }
      }
    }

    const intelligence = extractContactIntelligence(allActions, transcript);

    // Enhance with prospect data if available
    if (prospectData) {
      if (!intelligence.company && prospectData.company) {
        intelligence.company = prospectData.company;
      }
      if (!intelligence.name && prospectData.name) {
        intelligence.name = prospectData.name;
      }
      if (!intelligence.phone && prospectData.phone) {
        intelligence.phone = prospectData.phone;
      }
      if (!intelligence.email && prospectData.email) {
        intelligence.email = prospectData.email;
      }
    }

    // Add phone fallback from Vapi data or action data
    if (!intelligence.phone && latestVapiData?.contactInfo?.phone) {
      intelligence.phone = latestVapiData.contactInfo.phone;
    }
    if (!intelligence.phone && actions[0]?.action_data?.phone_number) {
      intelligence.phone = actions[0].action_data.phone_number;
    }

    return intelligence;
  }, [
    actions,
    processedActions,
    vapiData,
    uniqueCallIds,
    prospectLists,
    contactKey,
  ]);

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'consent_callback':
      case 'follow_up_call':
        return <Phone className='h-4 w-4' />;
      case 'appointment_interest':
      case 'appointment_booked':
        return <Calendar className='h-4 w-4' />;
      case 'email_provided':
      case 'email_promise':
        return <Mail className='h-4 w-4' />;
      case 'message_taking':
        return <MessageSquare className='h-4 w-4' />;
      default:
        return <MessageSquare className='h-4 w-4' />;
    }
  };

  const getActionColor = (type: string) => {
    switch (type) {
      case 'consent_callback':
      case 'follow_up_call':
        return 'bg-blue-500/10 text-blue-700 border-blue-200';
      case 'appointment_interest':
      case 'appointment_booked':
        return 'bg-green-500/10 text-green-700 border-green-200';
      case 'email_provided':
      case 'email_promise':
        return 'bg-purple-500/10 text-purple-700 border-purple-200';
      case 'message_taking':
        return 'bg-orange-500/10 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-200';
    }
  };

  const formatActionType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const fetchVapiCallData = async (callId: string) => {
    if (vapiData[callId] || loadingCalls.has(callId)) return;

    // Check cache first
    const cached = getCachedVapiCall(callId);
    if (cached) {
      if (process.env.NODE_ENV === 'development') {
        console.debug(
          `[ContactCard] Using cached Vapi data for call: ${callId}`,
        );
      }
      setVapiData((prev) => ({
        ...prev,
        [callId]: cached,
      }));
      return;
    }

    setLoadingCalls((prev) => new Set(prev).add(callId));

    try {
      if (process.env.NODE_ENV === 'development') {
        console.debug(
          `[ContactCard] Fetching fresh Vapi data for call: ${callId}`,
        );
      }

      const { data, error } = await supabase.functions.invoke(
        'fetch-vapi-call-data',
        {
          body: { callId },
        },
      );

      if (error) throw error;

      // Cache the response
      setCachedVapiCall(callId, data);

      setVapiData((prev) => ({
        ...prev,
        [callId]: data,
      }));
    } catch (error: any) {
      console.error('Error fetching Vapi data:', error);

      // Store error state but don't show intrusive toasts for recording data fetch failures
      setVapiData((prev) => ({
        ...prev,
        [callId]: {
          error: true,
          errorMessage: error?.message || 'Recording data unavailable',
          recordingUrl: null,
          transcript: null,
        },
      }));

      // Only show toast for critical auth/configuration errors that need user attention
      if (error?.status === 401 || error?.status === 403) {
        toast({
          title: 'Authentication Error',
          description:
            'Unable to access call recordings. Please check your VAPI API key configuration.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoadingCalls((prev) => {
        const next = new Set(prev);
        next.delete(callId);
        return next;
      });
    }
  };

  const handleOpenCallDetails = async (callId: string) => {
    if (!vapiData[callId]) {
      await fetchVapiCallData(callId);
    }
    setOpenCallDetailsId(callId);
  };

  const addTag = () => {
    if (newTag.trim() && !contactTags.includes(newTag.trim())) {
      setContactTags([...contactTags, newTag.trim()]);
      setNewTag('');
      setShowTagInput(false);
    }
  };

  const removeTag = (tag: string) => {
    setContactTags(contactTags.filter((t) => t !== tag));
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const latestAction = actions.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )[0];

  const isInbound = actions.some((a) => a.mode === 'inbound');
  const actionCount = actions.length;

  // Check if this is a qualified lead (has email OR follow-up actions) - include processed actions
  const allActions = [...actions, ...processedActions];
  const hasEmailAction = allActions.some(
    (a) => a.type === 'email_provided' || a.type === 'email_promise',
  );
  const hasFollowUpAction = allActions.some(
    (a) => a.type === 'follow_up_call' || a.type === 'consent_callback',
  );
  const isQualifiedLead = hasEmailAction || hasFollowUpAction;

  return (
    <Card className='hover:shadow-md transition-shadow'>
      <CardHeader className='pb-3'>
        <div className='flex items-start justify-between'>
          <div className='flex items-center gap-3 flex-1'>
            <Avatar className='h-12 w-12'>
              <AvatarFallback
                className={`${isArchived ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'} font-semibold`}
              >
                {getInitials(contactIntelligence?.name)}
              </AvatarFallback>
            </Avatar>

            <div className='flex-1'>
              <CardTitle className='text-lg flex items-center gap-2'>
                {contactIntelligence?.name || 'Unknown Contact'}
                {isArchived && (
                  <Badge
                    variant='outline'
                    className='bg-muted text-muted-foreground border-muted-foreground'
                  >
                    <Archive className='h-3 w-3 mr-1' />
                    Archived
                  </Badge>
                )}
                {!isArchived && actions[0]?.campaign?.client_name && (
                  <Badge
                    variant='outline'
                    className='bg-blue-500/10 text-blue-700 border-blue-200'
                  >
                    <Building2 className='h-3 w-3 mr-1' />
                    {actions[0].campaign.client_name}
                  </Badge>
                )}
                {!isArchived && isInbound && (
                  <Badge
                    variant='outline'
                    className='bg-success/10 text-success border-success'
                  >
                    <PhoneIncoming className='h-3 w-3 mr-1' />
                    Inbound
                  </Badge>
                )}
                {!isArchived && !isInbound && isQualifiedLead && (
                  <Badge
                    variant='outline'
                    className='bg-orange-500/10 text-orange-700 border-orange-200'
                  >
                    <Hand className='h-3 w-3 mr-1' />
                    Hand Raised
                  </Badge>
                )}
              </CardTitle>

              {/* Archive-specific info */}
              {isArchived && actions[0]?.completed_at && (
                <div className='text-sm text-muted-foreground mt-1'>
                  Completed:{' '}
                  {new Date(actions[0].completed_at).toLocaleDateString()}
                  {actions[0]?.archived_until && (
                    <span className='ml-2'>
                      • Expires:{' '}
                      {new Date(actions[0].archived_until).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}

              {/* Business Intelligence Header */}
              {!isArchived &&
                contactIntelligence?.businessHeader &&
                contactIntelligence.businessHeader !==
                  contactIntelligence.name && (
                  <div
                    className={`text-sm font-medium ${
                      contactIntelligence.businessHeader
                        .toLowerCase()
                        .includes('consented to')
                        ? 'text-success'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {contactIntelligence.businessHeader.replace(
                      `${contactIntelligence.name} - `,
                      '',
                    )}
                  </div>
                )}

              {!isArchived && (
                <div className='space-y-1 mt-1'>
                  {/* Phone number display using centralized derivation */}
                  {(() => {
                    const phoneNumbers = derivePhones(
                      actions,
                      contactIntelligence?.phone || undefined,
                    );
                    const labels = getPhoneLabels(isInbound);

                    return (
                      <>
                        {phoneNumbers.primary && (
                          <div className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                            <Phone className='h-3 w-3' />
                            <span
                              className='cursor-pointer hover:underline'
                              onClick={() =>
                                window.open(`tel:${phoneNumbers.primary}`)
                              }
                            >
                              {labels.primary}: {phoneNumbers.primary}
                            </span>
                            {phoneNumbers.isDifferent && (
                              <span className='text-xs opacity-70'>
                                ({labels.differentHint})
                              </span>
                            )}
                          </div>
                        )}
                        {phoneNumbers.callerId &&
                          phoneNumbers.callerId !== phoneNumbers.primary && (
                            <div className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                              <PhoneIncoming className='h-3 w-3' />
                              <span
                                className='cursor-pointer hover:underline'
                                onClick={() =>
                                  window.open(`tel:${phoneNumbers.callerId}`)
                                }
                              >
                                {labels.secondary}: {phoneNumbers.callerId}
                              </span>
                            </div>
                          )}
                        {phoneNumbers.dialed &&
                          phoneNumbers.dialed !== phoneNumbers.primary && (
                            <div className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                              <PhoneOutgoing className='h-3 w-3' />
                              <span
                                className='cursor-pointer hover:underline'
                                onClick={() =>
                                  window.open(`tel:${phoneNumbers.dialed}`)
                                }
                              >
                                {labels.secondary}: {phoneNumbers.dialed}
                              </span>
                            </div>
                          )}
                      </>
                    );
                  })()}

                  {contactIntelligence?.email && (
                    <div className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                      <Mail className='h-3 w-3' />
                      <span
                        className='cursor-pointer hover:underline'
                        onClick={() =>
                          window.open(`mailto:${contactIntelligence.email}`)
                        }
                      >
                        {contactIntelligence.email}
                      </span>
                    </div>
                  )}

                  {contactIntelligence?.company && (
                    <div className='text-sm text-muted-foreground'>
                      {contactIntelligence.company}
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {!isArchived && (
                <div className='flex flex-wrap gap-1 mt-2'>
                  {contactTags.map((tag, index) => (
                    <Badge key={index} variant='outline' className='text-xs'>
                      <Tag className='h-3 w-3 mr-1' />
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className='ml-1 hover:text-destructive'
                      >
                        <X className='h-3 w-3' />
                      </button>
                    </Badge>
                  ))}

                  {showTagInput ? (
                    <Input
                      type='text'
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag()}
                      onBlur={addTag}
                      className='text-xs h-6 w-20'
                      placeholder='Tag...'
                      autoFocus
                    />
                  ) : (
                    <button
                      onClick={() => setShowTagInput(true)}
                      className='text-xs text-muted-foreground hover:text-foreground border border-dashed rounded px-2 py-1'
                    >
                      + Add tag
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className='flex items-center gap-2'>
            {!isArchived && (
              <Button
                variant='outline'
                size='sm'
                onClick={() => handleOpenCallDetails(uniqueCallIds[0])}
                className='gap-2'
                disabled={!uniqueCallIds[0]}
              >
                <Play className='h-4 w-4' />
                Call details
              </Button>
            )}

            {isArchived && onDelete && (
              <Button
                onClick={onDelete}
                size='sm'
                variant='destructive'
                className='gap-2'
              >
                <Trash2 className='h-4 w-4' />
                Delete
              </Button>
            )}

            <Button
              variant='ghost'
              size='sm'
              onClick={() => setIsExpanded(!isExpanded)}
              className='h-9 w-9 p-0'
            >
              {isExpanded ? (
                <ChevronUp className='h-4 w-4' />
              ) : (
                <ChevronDown className='h-4 w-4' />
              )}
            </Button>
          </div>
        </div>

        {/* Quick summary when collapsed */}
        {!isExpanded && !isArchived && latestAction && (
          <div className='text-sm text-muted-foreground space-y-1'>
            <div className='flex items-center justify-between'>
              <div>
                Call received: {formatTimestamp(latestAction.created_at)}
              </div>
              {(() => {
                const urgency = getUrgencyFromAction(latestAction);
                if (urgency) {
                  return (
                    <Badge
                      variant={urgency === 'ASAP' ? 'destructive' : 'secondary'}
                      className={`text-xs font-medium ${
                        urgency === 'ASAP'
                          ? 'bg-red-600 text-white border-red-600'
                          : urgency === 'Today'
                            ? 'bg-orange-500/10 text-orange-700 border-orange-200'
                            : 'bg-blue-500/10 text-blue-700 border-blue-200'
                      }`}
                    >
                      {urgency}
                    </Badge>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        )}
      </CardHeader>

      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleContent>
          <CardContent className='pt-0'>
            {isArchived ? (
              <div className='space-y-4'>
                <div className='text-sm text-muted-foreground'>
                  This contact has been archived. All actions are completed.
                </div>

                {/* Show archived actions summary */}
                <div className='space-y-2'>
                  {actions.map((action) => (
                    <div
                      key={action.id}
                      className='flex items-center gap-2 text-sm p-2 bg-muted/50 rounded'
                    >
                      {getActionIcon(action.type)}
                      <span className='flex-1'>
                        {formatActionType(action.type)}
                      </span>
                      {action.completed_at && (
                        <span className='text-xs text-muted-foreground'>
                          {new Date(action.completed_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Show organized summary for outbound actions, raw view for inbound */}
                {isInbound ? (
                  <InboundContactSummary
                    contactName={contactIntelligence.name}
                    contactPhone={contactIntelligence.phone}
                    contactEmail={contactIntelligence.email}
                    contactCompany={contactIntelligence.company}
                    actions={actions}
                    processedActions={processedActions}
                    onMarkActionDone={onMarkActionDone}
                    onToggleActionProcessed={onToggleActionProcessed}
                    onUpdateDueDate={onUpdateDueDate}
                    onOpenCallDetails={handleOpenCallDetails}
                  />
                ) : (
                  <OutboundContactSummary
                    actions={actions}
                    processedActions={processedActions}
                    contactInfo={{
                      name: contactIntelligence.name,
                      phone: contactIntelligence.phone,
                      email: contactIntelligence.email,
                      company: contactIntelligence.company,
                    }}
                    onMarkActionDone={onMarkActionDone}
                    onToggleActionProcessed={onToggleActionProcessed}
                    onUpdateDueDate={onUpdateDueDate}
                    onOpenCallDetails={handleOpenCallDetails}
                  />
                )}
              </>
            )}

            {/* Next Actions Section moved inside expanded area */}
            {!isArchived && (
              <div className='border-t pt-4 space-y-3'>
                <h4 className='font-semibold text-sm'>Actions</h4>
                <div className='flex gap-2'>
                  <Button size='sm' onClick={onArchive} className='gap-2'>
                    <Archive className='h-4 w-4' />
                    Mark as Done
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>

      {/* Call Details Modal */}
      <Dialog
        open={!!openCallDetailsId}
        onOpenChange={(open) => !open && setOpenCallDetailsId(null)}
      >
        <DialogContent className='sm:max-w-[800px] max-h-[90vh]'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <FileText className='h-4 w-4' />
              Call Details
            </DialogTitle>
          </DialogHeader>
          <div className='space-y-6'>
            {/* Expiration Notice */}
            <div className='bg-amber-50 border border-amber-200 rounded-lg p-3'>
              <div className='text-sm text-amber-800'>
                <strong>Note:</strong> Recordings and transcripts expire after
                30 days. Download them if you need longer retention.
              </div>
            </div>

            {/* Audio Player */}
            <div className='space-y-2'>
              <h4 className='font-medium'>Recording</h4>
              {openCallDetailsId &&
              vapiData[openCallDetailsId]?.recordingUrl ? (
                <audio controls className='w-full' preload='metadata'>
                  <source
                    src={vapiData[openCallDetailsId].recordingUrl}
                    type='audio/wav'
                  />
                  <source
                    src={vapiData[openCallDetailsId].recordingUrl}
                    type='audio/mpeg'
                  />
                  Your browser does not support the audio element.
                </audio>
              ) : (
                <div className='text-center py-4 text-muted-foreground border rounded-lg bg-muted/30'>
                  {loadingCalls.has(openCallDetailsId || '')
                    ? 'Loading recording...'
                    : 'No recording available'}
                </div>
              )}
            </div>

            {/* Transcript */}
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <h4 className='font-medium'>Transcript</h4>
                {openCallDetailsId &&
                  vapiData[openCallDetailsId]?.transcript && (
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        const transcript =
                          vapiData[openCallDetailsId].transcript;
                        const contactInfo = extractContactInfo(actions);
                        const filename = `transcript-${contactInfo.name || 'unknown'}-${openCallDetailsId}.txt`;

                        const blob = new Blob([transcript], {
                          type: 'text/plain',
                        });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                      className='flex items-center gap-2'
                    >
                      <Download className='h-4 w-4' />
                      Download
                    </Button>
                  )}
              </div>
              {openCallDetailsId && vapiData[openCallDetailsId]?.transcript ? (
                <div className='max-h-[40vh] overflow-y-auto border rounded-lg p-4 bg-muted/30'>
                  <pre className='whitespace-pre-wrap text-sm font-mono leading-relaxed'>
                    {vapiData[openCallDetailsId].transcript}
                  </pre>
                </div>
              ) : (
                <div className='text-center py-4 text-muted-foreground border rounded-lg bg-muted/30'>
                  {loadingCalls.has(openCallDetailsId || '')
                    ? 'Loading transcript...'
                    : 'No transcript available'}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function extractContactInfo(actions: CallAction[]): ContactInfo {
  // Try to extract contact info from various sources
  const info: ContactInfo = {};

  for (const action of actions) {
    // Extract from action_data
    if (action.action_data) {
      if (action.action_data.caller_name && !info.name) {
        info.name = action.action_data.caller_name;
      }
      if (action.action_data.phone_number && !info.phone) {
        info.phone = action.action_data.phone_number;
      }
      if (action.action_data.email && !info.email) {
        info.email = action.action_data.email;
      }
      if (action.action_data.company && !info.company) {
        info.company = action.action_data.company;
      }
    }

    // Extract from phrase using regex
    if (action.phrase && !info.email) {
      const emailMatch = action.phrase.match(
        /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/,
      );
      if (emailMatch) {
        info.email = emailMatch[0];
      }
    }

    // Extract name patterns from phrase
    if (action.phrase && !info.name) {
      const namePatterns = [
        /my name is ([A-Za-z\s]+)/i,
        /this is ([A-Za-z\s]+)/i,
        /i'm ([A-Za-z\s]+)/i,
      ];

      for (const pattern of namePatterns) {
        const match = action.phrase.match(pattern);
        if (match && match[1]) {
          info.name = match[1].trim();
          break;
        }
      }
    }
  }

  // Fallback to prospect_id if no name found
  if (!info.name && actions[0]) {
    info.name = `Prospect ${actions[0].prospect_id.slice(0, 8)}`;
  }

  return info;
}
